// The two lenses that change WHOSE data the app is showing, on the phone.
//
// Both already existed in AppContext and were reachable only from the desktop
// header (ui/desktop/components/DesktopHeader). On mobile there was no surface
// for either, with two consequences that read as bugs rather than gaps:
//
//   · A Super Admin could not reach a location they are not mapped to, so
//     "Add Room", "Add Wi-Fi" and the location-wide pushes existed on the phone
//     but only ever applied to their own handful of sites.
//   · MobileShell ALREADY DRAWS the "viewing as" badge. Turn the lens on at a
//     desk, pick the phone up, and there was no control to turn it off — the
//     badge explained the wrong account without offering a way out.
//
// ⚠️ THE TWO DO NOT STACK, and the copy has to say so. All Locations drops every
// user mapping, so a name chosen here would have nothing left to narrow; asking
// for "this user's rooms" inside someone else's location is what returned an
// empty list and printed "No rooms". AppContext already resolves it that way
// (browseAll wins over viewAsUser) — the picker is disabled to match, rather
// than silently ignored.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Switch, ListItemButton, ListItemText, Autocomplete, TextField, Alert,
  useTheme,
} from '@mui/material';
import { useApp } from '../../../core/state/AppContext';
import { getAllUsers, userLabel, userSearchText } from '../../../core/devices/deviceAdmin';
import { tokenFilter } from '../../../core/utils/search';
import { renderUserOption } from '../../shared/userOption';

export default function AdminViewSheet({ open, onClose }) {
  const theme = useTheme();
  const c = theme.sli;
  const {
    isSuperAdmin, allLocations, setAllLocations, viewAsUser, setViewAsUser,
    realUserId, locations,
  } = useApp();

  // 450+ users is not worth fetching for the sessions that never open this, so
  // the list is pulled the first time the sheet is actually shown.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!open || !isSuperAdmin || users.length) return;
    getAllUsers()
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [open, isSuperAdmin, users.length]);

  if (!isSuperAdmin) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px' } }}
    >
      <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>Admin view</DialogTitle>

      <DialogContent>
        <ListItemButton
          onClick={() => setAllLocations(!allLocations)}
          sx={{ bgcolor: c.button, borderRadius: '16px', p: 2, mb: 1.5, gap: 2 }}
        >
          <ListItemText
            primary="All locations"
            secondary={allLocations
              ? `On — every location on the server (${locations.length})`
              : 'Off — only the locations mapped to you'}
            primaryTypographyProps={{ sx: { color: c.text, fontSize: 17 } }}
            secondaryTypographyProps={{ sx: { color: `${c.text}B3`, fontSize: 13 } }}
          />
          <Switch
            checked={allLocations}
            onChange={(e) => setAllLocations(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </ListItemButton>

        <Box sx={{ px: 0.5, mb: 2 }}>
          <Typography sx={{ color: `${c.text}B3`, fontSize: 12 }}>
            Every room and device is shown, whoever owns it. User mappings are
            ignored, and the location list becomes long — use the search in the
            Locations tab.
          </Typography>
        </Box>

        <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 15, mb: 1 }}>
          View as user
        </Typography>

        <Autocomplete
          size="small"
          disabled={allLocations}
          options={users}
          value={allLocations ? null : viewAsUser}
          onChange={(_, v) => setViewAsUser(v)}
          getOptionLabel={userLabel}
          isOptionEqualToValue={(o, v) => String(o.UserId) === String(v.UserId)}
          // Word-order independent: the stored name may be Surname-first while
          // the operator types it the other way round. See core/utils/search.
          filterOptions={tokenFilter(userSearchText)}
          // ⚠️ Supplies the KEY. A name is not unique — five accounts share
          // some labels — and MUI would otherwise key on the label. See
          // ui/shared/userOption.
          renderOption={renderUserOption}
          renderInput={(p) => (
            <TextField
              {...p}
              placeholder={allLocations ? 'Ignored — all locations is on' : 'Type a name, mobile or id…'}
              sx={{
                '& .MuiInputBase-root': { color: c.text },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: `${c.text}55` },
                '& .MuiSvgIcon-root': { color: c.text },
                '& input::placeholder': { color: `${c.text}99`, opacity: 1 },
              }}
            />
          )}
        />

        {viewAsUser && !allLocations && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {`Everything in the app is ${userLabel(viewAsUser)}'s data, not yours.`
              + ` You are user ${realUserId}.`}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {(allLocations || viewAsUser) && (
          <Button
            onClick={() => { setAllLocations(false); setViewAsUser(null); }}
            sx={{ color: c.text }}
          >
            Back to me
          </Button>
        )}
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: c.button, color: c.text }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
